import PropTypes from 'prop-types';
/**
 * Remove specific form fields from form data and update the state with the modified data.
 * 
 * @param {object} formData - The current form data object.
 * @param {Array} fieldsToRemove - An array of field dataField IDs to remove from the form data.
 */
export const removeFormFields = (formData, fieldsToRemove) => {
    // Create a copy of the formData object
    const modifiedData = { ...formData };

    // If formFields are directly in formData, filter them
    if (modifiedData.formFields) {
        modifiedData.formFields = modifiedData.formFields.filter((field) => !fieldsToRemove.includes(field.dataField));
    }

    // Normalize fieldsToRemove to lowercase for case-insensitive comparison
    const lowerCaseFieldsToRemove = fieldsToRemove.map((field) => field?.toLowerCase());

    // If there are sections, iterate over each to filter fields and nested fields
    if (modifiedData.section) {
        modifiedData.section = modifiedData.section.map((section) => {
            // Remove fields at the section level
            const updatedSection = { ...section };
            if (updatedSection.fields) {
                updatedSection.fields = updatedSection.fields.filter((field) => !lowerCaseFieldsToRemove.includes(field.dataField?.toLowerCase()));
            }

            // If there are rowGroups, filter fields within each row
            if (updatedSection.rowGroup) {
                updatedSection.rowGroup = updatedSection.rowGroup.map((row) => {
                    const updatedRow = { ...row };
                    updatedRow.fields = updatedRow.fields.filter(
                        (field) => !fieldsToRemove.includes(field.id)
                    );
                    return updatedRow;
                });
            }

            return updatedSection;
        });
    }

    // Return the modified form data
    return modifiedData;

};
// Define propTypes for the function parameters
removeFormFields.propTypes = {
    formData: PropTypes.object.isRequired,
    fieldsToRemove: PropTypes.array.isRequired
};

/**
 * Remove specific form fields from form data and update the state with the modified data.
 * 
 * @param {object} formFieldConfiguration - The current form data object.
 * @param {Array} fieldsToRemove - An array of field dataField IDs to remove from the form data.
 */
export const addFormFields = (formFieldConfiguration, fieldsToAdd) => {

    // Create a copy of the formFieldConfiguration object
    const modifiedData = { ...formFieldConfiguration };

    // Helper function to add the field to a section or rowGroup
    const addFieldToSectionOrGroup = (section, field) => {
        if (section.fields) {
            section.fields.push(field); // Add field to section's fields
        } else {
            section.fields = [field]; // Create a new fields array if not present
        }

        // Check if rowGroups exist and add the field to them as well
        if (section.rowGroup) {
            section.rowGroup.forEach((group) => {
                if (group.fields) {
                    group.fields.push(field); // Add field to row group's fields
                } else {
                    group.fields = [field]; // Create a new fields array if not present
                }
            });
        }
    };

    fieldsToAdd.forEach((fieldId) => {
        // Find the field in the existing configuration
        const field = findFieldsById(modifiedData, fieldId);

        if (field) {
            // Add the field to all relevant sections/rowGroups
            modifiedData.section.forEach((section) => {
                addFieldToSectionOrGroup(section, field);
            });
        }
    });

    // Update the state with the modified form data
    return modifiedData;
};

// Define propTypes for the function parameters
addFormFields.propTypes = {
    formFieldConfiguration: PropTypes.object.isRequired,
    fieldsToAdd: PropTypes.array.isRequired
};


/**
 * Finds a field by its ID from a given form field configuration.
 * @param {Object} formFieldConfiguration - The configuration object containing form data.
 * @param {string} fieldId - The ID of the field to search for.
 * @returns {Object|null} - The found field or null if not found.
 */
export const findFieldsById = (formFieldConfiguration, fieldId) => {
    if (!formFieldConfiguration?.section) {
        return null; // Return null if the configuration or sections are invalid.
    }

    for (const section of formFieldConfiguration.section) {
        // Check if field is directly in section.fields
        const field = section.fields?.find(field => field.id === fieldId);
        if (field) return field;

        // Check if field is in any of the rowGroups
        const groupField = section.rowGroup?.flatMap(group => group.fields).find(field => field.id === fieldId);
        if (groupField) return groupField;
    }

    return null; // Return null if the field is not found.
}

